import { useState } from 'react';
import { Button, Typography } from '@mui/material';
import { TopBar } from '../ProductsPage';
import { checkPermissions, SHOPS_WRITE } from '../../shared/utils/permissions';
import { useTranslation } from 'react-i18next';
import { useUserPermissions } from '../../apis/user-details';
import { DataGridContainer } from '../RestaurantsPage/tabs/Info';
import { GridColDef } from '@mui/x-data-grid';
import DataGrid from '../../shared/DataGrid/StyledDataGrid';
import HolidayDialog from './HolidayDialog';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomSnackBar } from '../../shared/SnackBar/useCustomSnackBar';
import {
  removePublicHolidayPricingDate,
  shopsApiQueryKeys,
  useAllPublicHolidayPricingDates,
} from '../../apis/shops-api';

export enum deliveryPricing {
  Normal = 0,
  Low = 1,
}

export type Holiday = {
  id?: number;
  date: string;
  name: string;
  localName?: string;
  isLunchOffer: boolean;
  preorderDeliveryPricing: deliveryPricing;
};

type Row = Holiday & {
  id: string;
};

const HolidaysPage: React.FC = () => {
  const [isHolidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Holiday | undefined>(undefined);
  const { data: exceptionDates } = useAllPublicHolidayPricingDates();

  const { t } = useTranslation();
  const { roles: userPermissions } = useUserPermissions();
  const queryClient = useQueryClient();
  const snackBar = useCustomSnackBar();

  const handleNewHoliday = () => {
    setSelected(undefined);
    setHolidayDialogOpen(true);
  };

  const handleRemoveClick = async (date: Holiday) => {
    if (!date.id) return;

    await removePublicHolidayPricingDate(date.id);
    await queryClient.invalidateQueries([shopsApiQueryKeys.allExceptionDates]);
    snackBar.showSuccess(t('exceptionDate.snackbar.deleted'));
  };

  const columns: GridColDef<Row>[] = [
    {
      field: 'date',
      headerName: t('exceptionDate.list.date'),
      flex: 2,
      renderCell(params) {
        return (
          <Button
            onClick={() => {
              setSelected(params.row);
              setHolidayDialogOpen(true);
            }}
          >
            {dayjs(params.row.date).format('D.M.YYYY')}
          </Button>
        );
      },
    },
    {
      field: 'name',
      headerName: t('exceptionDate.list.localName'),
      flex: 1,
    },
    /* {
      field: 'isLunchOffer',
      headerName: t('exceptionDate.list.isLunchOffer'),
      flex: 1,
      renderCell: (params) => {
        return (params.value as boolean) ? (
          <Chip color="primary" label={t('shared.activeLabel.active')} />
        ) : (
          <Chip color="error" label={t('shared.activeLabel.inactive')} />
        );
      },
    }, */
    {
      field: 'preorderDeliveryPricing',
      headerName: t('exceptionDate.list.deliveryPricing'),
      flex: 1,
      renderCell: (params) => t(`exceptionDate.deliveryPricing.${params.value}`),
    },
    {
      field: 'remove',
      headerName: '',
      align: 'right',
      flex: 1,
      renderCell: (params) => (
        <Button onClick={() => handleRemoveClick(params.row)} color="error">
          {t('global.delete')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <TopBar>
        <Typography variant="h1">{t('exceptionDate.incomingHolidays')}</Typography>
        <Button
          disabled={!checkPermissions(userPermissions || [], [SHOPS_WRITE])}
          variant="contained"
          onClick={handleNewHoliday}
          color="primary"
        >
          {t('exceptionDate.addExceptionDate')}
        </Button>
      </TopBar>
      {!exceptionDates ? (
        <p>{t('global.loading')}</p>
      ) : (
        <DataGridContainer>
          <DataGrid
            columns={columns}
            rows={exceptionDates.map(
              (r) =>
                ({
                  ...r,
                  id: r.id?.toString() || r.date,
                } as Row)
            )}
          />
        </DataGridContainer>
      )}
      <HolidayDialog
        open={isHolidayDialogOpen}
        onDialogClose={() => setHolidayDialogOpen(false)}
        exceptionDate={selected}
      />
    </>
  );
};

export default HolidaysPage;
